import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule, SlicePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '../../../services/api.service';
import { ToasterService } from '../../../services/toaster.service';
import { CachingService } from '../../../services/caching.service';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { routes } from '../../../constants/routes';
import {
  GlobalSearchResponse,
  PopularTag,
  RecentSearch,
  SearchResultItemData,
  SearchTab,
  SearchTypes,
  TrendingSearch,
} from '../../../models/global-search.model';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';

// Shared components
import { SearchInputComponent } from '../search-input/search-input.component';
import { SearchTabsComponent } from '../search-tabs/search-tabs.component';
import { GlobalSearchResultSkeletonLoaderComponent } from './global-search-result-skeleton-loader/global-search-result-skeleton-loader.component';
import {
  SearchEmptyStateComponent,
  SearchSuggestion,
} from '../search/search-empty-state/search-empty-state.component';
import { SearchNoResultsComponent } from '../search/search-no-results/search-no-results.component';
import { SearchKeyboardHintsComponent } from '../search/search-keyboard-hints/search-keyboard-hints.component';
import { SearchResultSectionComponent } from '../search/search-result-section/search-result-section.component';

// Type icons
import {
  articleIconSVG,
  datasetIconSVG,
  modelIconSVG,
  organizationIconSVG,
  toolkitIconSVG,
  tryoutIconSVG,
  tutorialIconSVG,
  usecaseIconSVG,
} from '../../../constants/svg/search-type-icons';

function toItems(
  list: any[] | undefined,
  map: (item: any) => SearchResultItemData,
): SearchResultItemData[] {
  return list?.map(map) ?? [];
}

interface ResultSection {
  title: string;
  fragment: string;
  count: number;
  items: SearchResultItemData[];
  icon: string | null;
  startIndex: number;
}
import { RequestArtefactBannerComponent } from '../../layout/request-artefact-banner/request-artefact-banner.component';
import { RequestArtefactDialogComponent } from '../request-artefact-dialog/request-artefact-dialog.component';

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [
    CommonModule,
    SlicePipe,
    SearchInputComponent,
    SearchTabsComponent,
    GlobalSearchResultSkeletonLoaderComponent,
    SearchEmptyStateComponent,
    SearchNoResultsComponent,
    SearchKeyboardHintsComponent,
    SearchResultSectionComponent,
    RequestArtefactBannerComponent
  ],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent implements OnInit, OnDestroy, ApiErrorHandler {
  isLoadingResults = false;
  searchText = '';
  activeTab: SearchTypes = SearchTypes.all;

  focusedIndex = -1;

  recentSearches: RecentSearch[] = [];
  trendingSearches: TrendingSearch[] = [];
  popularTags: PopularTag[] = [];

  hasResults = false;
  flatItems: SearchResultItemData[] = [];
  visibleSections: ResultSection[] = [];

  readonly recentSearchCacheKey = 'recent-searches';
  readonly routes = routes;
  readonly SearchTypes = SearchTypes;

  readonly icons = {
    dataset: datasetIconSVG,
    model: modelIconSVG,
    usecase: usecaseIconSVG,
    toolkit: toolkitIconSVG,
    article: articleIconSVG,
    organization: organizationIconSVG,
    tutorial: tutorialIconSVG,
    tryout: tryoutIconSVG,
  } as const;

  readonly searchTabs: SearchTab[] = [
    { label: 'All',           value: SearchTypes.all          },
    { label: 'Datasets',      value: SearchTypes.dataset      },
    { label: 'Models',        value: SearchTypes.model        },
    { label: 'AI Tryouts',    value: SearchTypes.tryout       },
    { label: 'Use Cases',     value: SearchTypes.usecase      },
    { label: 'Toolkits',      value: SearchTypes.toolkit      },
    { label: 'Articles',      value: SearchTypes.article      },
    { label: 'Organizations', value: SearchTypes.organization },
  ];

  private _searchResults: GlobalSearchResponse | null = null;
  private fetchResultsSubs?: Subscription;

  get searchResults(): GlobalSearchResponse | null {
    return this._searchResults;
  }

  @Output() onClose = new EventEmitter<void>();

  onRequestArtefact(): void {
    this.matDialog.open(RequestArtefactDialogComponent, {
      width: '600px',
      disableClose: true,
      autoFocus: false,
      data: { requirement: undefined },
    });
    this.onClose.emit();
  }


  constructor(
    private readonly router: Router,
    public  readonly toaster: ToasterService,
    private readonly apiService: ApiService,
    private readonly cachingService: CachingService,
    private readonly matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.recentSearches = this.loadRecentSearches();
    this.loadMetaData();
  }

  ngOnDestroy(): void {
    this.fetchResultsSubs?.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.matDialog.openDialogs.length > 0) return;
    this.onClose.emit();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.matDialog.openDialogs.length > 0) return;

    const flat = this.flatItems;

    switch (event.key) {
      case 'ArrowDown':
        if (!flat.length) return;
        event.preventDefault();
        this.focusedIndex =
          this.focusedIndex < flat.length - 1 ? this.focusedIndex + 1 : 0;
        break;

      case 'ArrowUp':
        if (!flat.length) return;
        event.preventDefault();
        this.focusedIndex =
          this.focusedIndex > 0 ? this.focusedIndex - 1 : flat.length - 1;
        break;

      case 'Tab':
        event.preventDefault();
        this.cycleTab(event.shiftKey);
        break;

      case 'Enter':
        if (this.focusedIndex >= 0 && flat[this.focusedIndex]) {
          event.preventDefault();
          this.viewArtifactDetails(flat[this.focusedIndex]);
        }
        break;
    }
  }

  private cycleTab(reverse = false): void {
    const idx  = this.searchTabs.findIndex((t) => t.value === this.activeTab);
    const next = reverse
      ? (idx - 1 + this.searchTabs.length) % this.searchTabs.length
      : (idx + 1) % this.searchTabs.length;
    this.onTabChange(this.searchTabs[next].value);
  }

  private loadMetaData(type: SearchTypes = this.activeTab): void {
    const trendingKey = `trending-searches-${type}`;
    const tagsKey     = `popular-tags-${type}`;

    const cachedTrending = this.cachingService.getCache<TrendingSearch[]>(trendingKey);
    const cachedTags     = this.cachingService.getCache<PopularTag[]>(tagsKey);

    if (cachedTrending) this.trendingSearches = cachedTrending;
    if (cachedTags)     this.popularTags      = cachedTags;
    if (cachedTrending && cachedTags) return;

    if (!cachedTrending) {
      this.apiService.fetchTrendingSearches(type).subscribe({
        next: (data) => {
          this.trendingSearches = data ?? [];
          this.cachingService.setCache(trendingKey, this.trendingSearches);
        },
        error: () => { this.trendingSearches = []; },
      });
    }

    if (!cachedTags) {
      this.apiService.fetchPopularTags(type).subscribe({
        next: (data) => {
          this.popularTags = data ?? [];
          this.cachingService.setCache(tagsKey, this.popularTags);
        },
        error: () => { this.popularTags = []; },
      });
    }
  }

  onSearchInput(text: string): void {
    this.searchText   = text.trim();
    this.focusedIndex = -1;
    if (this.searchText.length < 2) {
      this.setSearchResults(null);
      return;
    }
    this.fetchSearchResults(this.searchText, this.activeTab);
  }

  onSearchCleared(): void {
    this.searchText = '';
    this.setSearchResults(null);
  }

  onEnterPressed(text: string): void {
    const keyword = text.trim();
    if (keyword.length < 2) return;

    this.router.navigate(['/', routes.home, routes.search], {
      queryParams: { keyword },
      fragment: this.activeTab,
    });
    this.onClose.emit();
  }

  onAskAssistant(_query: string): void {
    this.onClose.emit();
  }

  onTabChange(tab: string): void {
    this.activeTab    = tab as SearchTypes;
    this.focusedIndex = -1;

    if (this.searchText.length >= 2) {
      this.fetchSearchResults(this.searchText, this.activeTab);
    } else {
      this.loadMetaData(this.activeTab);
    }
  }

  onSuggestionSelected(suggestion: SearchSuggestion): void {
    this.searchText = suggestion.text;
    this.activeTab  = suggestion.type;
    this.fetchSearchResults(suggestion.text, suggestion.type);
  }

  private fetchSearchResults(text: string, type: SearchTypes): void {
    this.fetchResultsSubs?.unsubscribe();
    this.isLoadingResults = true;
    this.setSearchResults(null);

    this.fetchResultsSubs = this.apiService
      .fetchGlobalSearchResults(text, type)
      .pipe(finalize(() => (this.isLoadingResults = false)))
      .subscribe({
        next: (results) => {
          this.setSearchResults(results);
          this.cachingService.setCache(
            this.cachingService.globalSearchResultsKey,
            results,
          );
          this.persistRecentSearch(text, type);
        },
        error: (error) => {
          this.setSearchResults(null);
          apiErrorHandler.call(this, error);
        },
      });
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  viewArtifactDetails(item: SearchResultItemData): void {
    if (item.route) {
      this.router.navigate(item.route);
    }
    this.onClose.emit();
  }

  navigateToViewAll(fragment: string): void {
    this.router.navigate(['/', routes.home, routes.search], {
      queryParams: { keyword: this.searchText },
      fragment,
    });
    this.onClose.emit();
  }

  // ── Recent searches ────────────────────────────────────────────────────────

  onRemoveRecent(search: RecentSearch): void {
    this.recentSearches = this.recentSearches.filter(
      (s) => !(s.text === search.text && s.category === search.category),
    );
    this.persistRecentSearchList();
  }

  onClearAllRecent(): void {
    this.recentSearches = [];
    localStorage.removeItem(this.recentSearchCacheKey);
  }

  private loadRecentSearches(): RecentSearch[] {
    try {
      return (
        (JSON.parse(
          localStorage.getItem(this.recentSearchCacheKey) ?? 'null',
        ) as RecentSearch[]) ?? []
      );
    } catch {
      return [];
    }
  }

  private persistRecentSearch(text: string, category: SearchTypes): void {
    const isDuplicate = this.recentSearches.some(
      (s) => s.text === text && s.category === category,
    );
    if (isDuplicate) return;

    this.recentSearches = [
      { text, category },
      ...this.recentSearches.slice(0, 7),
    ];
    this.persistRecentSearchList();
  }

  private persistRecentSearchList(): void {
    localStorage.setItem(
      this.recentSearchCacheKey,
      JSON.stringify(this.recentSearches),
    );
  }


  private setSearchResults(value: GlobalSearchResponse | null): void {
    this._searchResults = value;

    if (!value) {
      this.hasResults      = false;
      this.flatItems       = [];
      this.visibleSections = [];
      return;
    }


    const datasetItems = toItems(value.dataset?.list, (d) => ({
      title:          d.name,
      shortDescription: d.shortDescription ?? d.short_description ?? '',
      logo:           d.imageUrl ?? d.image_url,
      views:          d.stats?.views ?? d.views,
      updatedDate:    d.updatedAt ?? d.updated_date,
      tags:           d.tags,
      route:          d.route ?? (d.slug ? [routes.home, routes.datasets, d.slug] : undefined),
      artifactType:   d.artifact_type,
      score:          d.score,
      rating:         d.rating,
      sourceOrgName:  d.source_org_name ?? d.sourceOrgName,
    }));

    const modelItems = toItems(value.model?.list, (m) => ({
      title:            m.name,
      shortDescription: m.short_description ?? m.description ?? '',
      logo:             m.imageUrl ?? m.image_url,
      views:            m.views,
      updatedDate:      m.updated_date ?? m.updatedAt,
      tags:             m.tags,
      route:            m.route,
    }));

    const tryoutItems = toItems(value.model_tryout?.list, (t) => ({
      title:            t.name,
      shortDescription: t.shortDescription ?? t.short_description ?? '',
      logo:             t.imageUrl ?? t.image_url ?? null,
      views:            t.stats?.views ?? t.views ?? 0,
      updatedDate:      t.updatedAt ?? t.updated_date,
      tags:             t.tags,
      score:            t.score,
      sourceOrgName:    t.source_org_name ?? t.sourceOrgName,
      route:            t.route,
    }));

    const usecaseItems = toItems(value.usecase?.list, (u) => ({
      title:            u.name,
      shortDescription: u.short_description ?? u.shortDescription ?? '',
      logo:             u.imageUrl ?? u.image_url,
      views:            u.views,
      updatedDate:      u.updated_date ?? u.updatedAt,
      tags:             u.tags,
      route:            u.route,
    }));

    const toolkitItems = toItems(value.toolkit?.list, (t) => ({
      title:            t.name,
      shortDescription: t.description ?? t.short_description ?? '',
      logo:             t.logo,
      views:            t.views,
      updatedDate:      t.updated_date ?? t.updatedAt,
      route:            t.route,
    }));

    const articleItems = toItems(value.article?.list, (a) => ({
      title:            a.title ?? a.name,
      shortDescription: a.short_description ?? '',
      logo:             a.banner_url ?? a.imageUrl,
      route:            a.route,
    }));

    const organizationItems = toItems(value.organization?.list, (o) => ({
      title:            o.name,
      shortDescription: null,
      logo:             o.logo ?? o.logoUrl,
      route:            o.route,
    }));

    // ── Derived state ────────────────────────────────────────────────────────

    this.hasResults = !!(
      value.dataset?.count       ||
      value.model?.count         ||
      value.usecase?.count       ||
      value.toolkit?.count       ||
      value.article?.count       ||
      value.organization?.count  ||
      value.model_tryout?.count
    );

    this.flatItems = [
      ...datasetItems,
      ...modelItems,
      ...usecaseItems,
      ...toolkitItems,
      ...articleItems,
      ...organizationItems,
    ];

    const d = datasetItems.length;
    const m = modelItems.length;
    const u = usecaseItems.length;
    const t = toolkitItems.length;
    const a = articleItems.length;

    const offsets: Record<string, number> = {
      dataset:      0,
      model:        d,
      usecase:      d + m,
      toolkit:      d + m + u,
      article:      d + m + u + t,
      organization: d + m + u + t + a,
      tryout:       0,
    };

    // Build the full section list, then filter to the active tab
    const allSections: ResultSection[] = [
      {
        title: 'Datasets',      fragment: 'dataset',
        count: value.dataset?.count      ?? 0, items: datasetItems,
        icon:  this.icons.dataset,             startIndex: offsets['dataset'],
      },
      {
        title: 'Models',        fragment: 'model',
        count: value.model?.count        ?? 0, items: modelItems,
        icon:  this.icons.model,               startIndex: offsets['model'],
      },
      {
        title: 'AI Tryouts',    fragment: 'tryout',
        count: value.model_tryout?.count ?? 0, items: tryoutItems,
        icon:  this.icons.tryout,              startIndex: offsets['tryout'],
      },
      {
        title: 'Use Cases',     fragment: 'usecase',
        count: value.usecase?.count      ?? 0, items: usecaseItems,
        icon:  this.icons.usecase,             startIndex: offsets['usecase'],
      },
      {
        title: 'Toolkits',      fragment: 'toolkit',
        count: value.toolkit?.count      ?? 0, items: toolkitItems,
        icon:  this.icons.toolkit,             startIndex: offsets['toolkit'],
      },
      {
        title: 'Articles',      fragment: 'article',
        count: value.article?.count      ?? 0, items: articleItems,
        icon:  this.icons.article,             startIndex: offsets['article'],
      },
      {
        title: 'Organizations', fragment: 'organization',
        count: value.organization?.count ?? 0, items: organizationItems,
        icon:  this.icons.organization,        startIndex: offsets['organization'],
      },
    ];

    this.visibleSections = allSections.filter(
      (s) =>
        s.count > 0 &&
        (this.activeTab === SearchTypes.all || this.activeTab === (s.fragment as SearchTypes)),
    );
  }

  get isEmptyState(): boolean {
    return (
      !this.isLoadingResults &&
      this.searchText.length >= 2 &&
      !this.hasResults
    );
  }

  get showInitialState(): boolean {
    return !this.isLoadingResults && this.searchText.length < 2;
  }
}
